/**
 * A cluster's properties as `/v3/api/labelClusters` sends them — the fields the tool reads; the feed carries more
 * (`region_id`, `osm_way_id`, the date averages, …).
 * @typedef {object} AccessScoreClusterProps
 * @property {number} label_cluster_id
 * @property {string} label_type
 * @property {number} street_edge_id - The street the cluster sits on.
 * @property {?number} intersection_id - The crossing a corner-type cluster scores toward; null for a segment's.
 * @property {number[]} [coordinates] - The dot's `[lng, lat]`, from the feature's geometry, when the layer read it.
 * @property {?number} median_severity - The labels' median rating, 1–3, or null for an unrated type.
 * @property {number} agree_count
 * @property {number} disagree_count
 * @property {number} cluster_size - How many labels the cluster holds.
 * @property {number[]} [label_ids] - Those labels' ids. This and the two below are absent only when the layer
 *     could not parse them back out of Mapbox's tiles (see `#props`).
 * @property {string[]} [users] - The ids of the users who placed them.
 * @property {Record<string, number>} [tag_counts] - Labels per tag.
 */

import mapboxgl from '../common/mapboxgl.js';
import { util } from '../common/utilities.js';
import '../common/utilitiesSidewalk.js';

/**
 * The label clusters a score is computed from, drawn on the AccessScore map (#5217).
 *
 * The engine scores **clusters**, not raw labels: `AccessScoreService` streams one row per cluster and
 * `AccessScoreCalculator` turns each into a term. Drawing the raw labels instead would show a different, denser
 * population than the arithmetic uses — three pins on one broken curb read as three problems on the map and one
 * on the street's score. So this layer draws the clusters themselves, one Mapbox circle layer per scored type,
 * with a dot's radius carrying how many labels went into it.
 *
 * Presentation only: it takes a FeatureCollection from `/v3/api/labelClusters` and hands hover and click back to
 * the page, which owns the model and the label card.
 */
export class AccessScoreClusterLayer {
  /** Cluster sizes above this stop growing the dot; a 40-label cluster shouldn't swallow the street. */
  static #MAX_SIZE = 10;

  #map;
  #types;
  #layers = [];
  #tooltip;
  #tooltipHtml;
  #onSelect;
  #onHover;
  #visible = true;
  /**
   * The last collection drawn, kept so a basemap swap can redraw it.
   * @type {GeoJSON.FeatureCollection}
   */
  #data = { type: 'FeatureCollection', features: [] };
  /** The dot under the pointer, as `{source, id}` for feature-state, or null. */
  #hovered = null;
  /** Dots lit together from the street panel (a crossing's clusters), as `{source, id}` each. */
  #highlighted = [];

  /**
   * @param {mapboxgl.Map} map - A loaded Mapbox map.
   * @param {object} options - Data and callbacks.
   * @param {string[]} options.types - The scored label types, in the engine's order (bottom layer first).
   * @param {(props: AccessScoreClusterProps) => ?string} options.tooltipHtml - A cluster's `properties` in, its
   *   tooltip's HTML out, or null for no tooltip.
   * @param {(props: AccessScoreClusterProps) => void} options.onSelect - Called on a click, with its `properties`.
   * @param {(props: ?AccessScoreClusterProps) => void} [options.onHover] - Called when the pointer lands on a dot,
   *   with its `properties`, and with null when it leaves (#5748).
   */
  constructor(map, { types, tooltipHtml, onSelect, onHover = () => {} }) {
    this.#map = map;
    this.#types = types;
    this.#tooltipHtml = tooltipHtml;
    this.#onSelect = onSelect;
    this.#onHover = onHover;
    this.#tooltip = new mapboxgl.Popup({
      closeButton: false, closeOnClick: false, focusAfterOpen: false, className: 'acs-tooltip', maxWidth: '280px',
    });
    for (const type of types) this.#layers.push(this.#addLayer(type));
    this.#addInteractions();
  }

  /**
   * Replaces the clusters drawn, per type. Types absent from the collection are emptied rather than left stale.
   * @param {GeoJSON.FeatureCollection} featureCollection - A `/v3/api/labelClusters` response.
   */
  setData(featureCollection) {
    this.#data = featureCollection;
    const byType = Object.fromEntries(this.#types.map((type) => [type, []]));
    for (const feature of featureCollection.features || []) {
      byType[feature.properties.label_type]?.push(feature);
    }
    for (const type of this.#types) {
      this.#map.getSource(AccessScoreClusterLayer.#layerId(type))
        .setData({ type: 'FeatureCollection', features: byType[type] });
    }
  }

  /**
   * The clusters drawn, per type, in the shape `createNearbyLabelNavigator` tours (#5748): a nearby-cluster
   * navigator reads this on every step, so it is the live collection, not a copy.
   * @returns {Record<string, Array<GeoJSON.Feature<GeoJSON.Point>>>} Features by label type.
   */
  get sortedClusters() {
    /** @type {Record<string, Array<GeoJSON.Feature<GeoJSON.Point>>>} */
    const byType = Object.fromEntries(this.#types.map((type) => [type, []]));
    for (const feature of this.#data.features || []) {
      byType[feature.properties.label_type]?.push(/** @type {GeoJSON.Feature<GeoJSON.Point>} */ (feature));
    }
    return byType;
  }

  /**
   * One drawn cluster's properties, as a click would hand them over, coordinates included.
   * @param {number} clusterId - A `label_cluster_id`.
   * @returns {?AccessScoreClusterProps} Null when the cluster is not among those drawn.
   */
  cluster(clusterId) {
    const feature = (this.#data.features || []).find((f) => f.properties.label_cluster_id === clusterId);
    return feature ? AccessScoreClusterLayer.#props(feature) : null;
  }

  /**
   * Rebuilds the sources and layers after a `map.setStyle`, with the last data and the visibility in force. The
   * pointer handlers are keyed by layer id and survive the swap, so they are not bound again.
   */
  remount() {
    for (const id of this.#layers) {
      if (this.#map.getLayer(id)) this.#map.removeLayer(id);
      if (this.#map.getSource(id)) this.#map.removeSource(id);
    }
    this.#hovered = null;
    this.#highlighted = [];
    this.#tooltip.remove();
    // The ids are the same strings, so the handlers bound to `#layers` keep matching.
    for (const type of this.#types) this.#addLayer(type);
    this.setData(this.#data);
    this.#applyVisibility();
  }

  /**
   * Shows or hides every cluster layer.
   * @param {boolean} visible - True to draw them.
   */
  setVisible(visible) {
    this.#visible = visible;
    this.#applyVisibility();
    if (!visible) {
      this.#clearHover();
      this.setHighlighted(null);
    }
  }

  #applyVisibility() {
    for (const id of this.#layers) this.#map.setLayoutProperty(id, 'visibility', this.#visible ? 'visible' : 'none');
  }

  /**
   * Lights a cluster's dot as the pointer would, for a hover that starts elsewhere (a tile in the street panel,
   * #5748), or puts it out. The tooltip stays down: the pointer is not on the map. Does not report through `onHover`,
   * which is for the pointer's own hovers.
   * @param {?AccessScoreClusterProps} props - The cluster, or null to clear.
   */
  setHovered(props) {
    if (this.#hovered) this.#map.setFeatureState(this.#hovered, { hover: false });
    this.#hovered = null;
    if (!props || !this.#visible) return;
    this.#hovered = { source: AccessScoreClusterLayer.#layerId(props.label_type), id: props.label_cluster_id };
    this.#map.setFeatureState(this.#hovered, { hover: true });
  }

  /**
   * Lights several dots at once, as the panel's crossing dot does for a crossing's clusters (#5748), replacing the
   * last such set; null or an empty list puts them out. Independent of the pointer's own hover.
   * @param {?AccessScoreClusterProps[]} list - The clusters, or null to clear.
   */
  setHighlighted(list) {
    for (const key of this.#highlighted) this.#map.setFeatureState(key, { hover: false });
    this.#highlighted = [];
    if (!list || !this.#visible) return;
    for (const props of list) {
      const key = { source: AccessScoreClusterLayer.#layerId(props.label_type), id: props.label_cluster_id };
      this.#highlighted.push(key);
      this.#map.setFeatureState(key, { hover: true });
    }
  }

  /**
   * Whether a cluster sits under a pointer event — the map's street/region tooltip yields to this one, and
   * a click on a dot must not also select the street beneath it.
   * @param {mapboxgl.MapMouseEvent} e - A Mapbox pointer event.
   * @returns {boolean} True when a visible cluster is under the pointer.
   */
  claims(e) {
    if (!this.#visible) return false;
    return this.#map.queryRenderedFeatures(e.point, { layers: this.#layers }).length > 0;
  }

  /** One source + circle layer for a type, sized by cluster size and colored by the type's canonical color. */
  #addLayer(type) {
    const id = AccessScoreClusterLayer.#layerId(type);
    this.#map.addSource(id, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      promoteId: 'label_cluster_id',
    });
    const colors = util.misc.getLabelColors();
    const hovered = ['boolean', ['feature-state', 'hover'], false];
    this.#map.addLayer({
      id,
      type: 'circle',
      source: id,
      layout: { visibility: 'visible' },
      paint: {
        'circle-radius': AccessScoreClusterLayer.#radius(hovered),
        'circle-color': colors[type].fillStyle,
        'circle-stroke-color': colors[type].strokeStyle,
        'circle-stroke-width': ['case', hovered, 2, 1],
        'circle-opacity': 0.85,
        'circle-stroke-opacity': 0.9,
      },
    });
    return id;
  }

  /**
   * Radius by zoom and cluster size. Size is the honest signal here — a cluster of eight labels is eight people
   * (or eight passes) agreeing something is there — so it is on the dot rather than only in the tooltip.
   *
   * Mapbox allows `zoom` only as the input of the *outermost* interpolate, so the size ramp and the hover bump
   * both live inside each zoom stop rather than being added around the whole expression.
   *
   * @param {Array} hovered - The feature-state hover test, added as a few extra pixels at every zoom.
   * @returns {Array} A Mapbox paint expression.
   */
  static #radius(hovered) {
    const bySize = (small, large) => ['+',
      ['interpolate', ['linear'], ['get', 'cluster_size'], 1, small, AccessScoreClusterLayer.#MAX_SIZE, large],
      ['case', hovered, 3, 0]];
    return ['interpolate', ['linear'], ['zoom'], 13, bySize(2, 4), 17, bySize(5, 11), 20, bySize(8, 18)];
  }

  #addInteractions() {
    this.#map.on('mousemove', this.#layers, (e) => {
      if (!this.#visible || !e.features.length) return;
      const feature = e.features[0];
      const props = AccessScoreClusterLayer.#props(feature);
      if (this.#hovered?.id !== feature.id || this.#hovered?.source !== feature.layer.id) {
        this.#clearHover();
        this.#hovered = { source: feature.layer.id, id: feature.id };
        this.#map.setFeatureState(this.#hovered, { hover: true });
        this.#map.getCanvas().style.cursor = 'pointer';
        this.#onHover(props);
      }
      const html = this.#tooltipHtml(props);
      if (html) this.#tooltip.setLngLat(e.lngLat).setHTML(html).addTo(this.#map);
    });
    this.#map.on('mouseleave', this.#layers, () => this.#clearHover());
    this.#map.on('click', this.#layers, (e) => {
      if (!this.#visible || !e.features.length) return;
      // The street or region under the dot keeps its selection; AccessScoreMapView checks defaultPrevented.
      e.preventDefault();
      this.#onSelect(AccessScoreClusterLayer.#props(e.features[0]));
    });
  }

  /**
   * A cluster's properties as the API sent them.
   *
   * Mapbox turns a GeoJSON source into vector tiles internally, and vector tiles carry only scalar values — so
   * `label_ids` and `tag_counts` come back out of a query or an event as **JSON strings**, silently. Reading
   * `properties.label_ids[0]` gets you the character `[`, not a label id, and the failure is a broken label card
   * rather than an error.
   *
   * @param {GeoJSON.Feature} feature - A Mapbox feature from a query or a pointer event.
   * @returns {AccessScoreClusterProps} The properties, with the JSON-encoded fields parsed back.
   */
  static #props(feature) {
    const props = { ...feature.properties };
    // The dot's own position, for a host that ties a card to it (the cluster sheet's beacon and tail, #5748).
    if (feature.geometry?.type === 'Point') props.coordinates = feature.geometry.coordinates;
    for (const key of ['label_ids', 'users', 'tag_counts']) {
      if (typeof props[key] === 'string') {
        try {
          props[key] = JSON.parse(props[key]);
        } catch {
          delete props[key];
        }
      }
    }
    return /** @type {AccessScoreClusterProps} */ (props);
  }

  #clearHover() {
    if (this.#hovered) {
      this.#map.setFeatureState(this.#hovered, { hover: false });
      this.#onHover(null);
    }
    this.#hovered = null;
    this.#tooltip.remove();
    this.#map.getCanvas().style.cursor = '';
  }

  static #layerId(type) {
    return `acs-clusters-${type}`;
  }
}
